import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { usePermissions } from '../../../contexts/PermissionContext';
import ContractDashboard from './ContractDashboard';
import ContractsList from './ContractsList';
import ThirdPartyList from '../third-parties/ThirdPartyList';


type TabKey = 'dashboard' | 'data' | 'third-parties';


// Filter params that only make sense on the Contracts tab; their presence
// auto-flips the active tab so dashboard KPI drill-downs and audit-log
// cross-links land the user in the right place without needing an explicit
// ?tab=data. 'id' is ambiguous (both Contracts and Third Parties use it for
// audit drill-in), so links carrying a third-party id must pass an explicit
// ?tab=third-parties — the Third Parties redirect does that.
const DATA_ONLY_PARAMS = [
  'id',
  'status',
  'category',
  'ending_before',
  'ending_after',
  'value_min',
  'value_max',
  'scope',
] as const;


export default function ContractsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasAccess, loading, permissions } = usePermissions();

  const canContracts = hasAccess('/compliance/contracts');
  const canThirdParties = hasAccess('/compliance/third-parties');

  const tabs = useMemo(
    () =>
      (
        [
          { id: 'dashboard', label: 'Dashboard', visible: canContracts },
          { id: 'data', label: 'Contracts', visible: canContracts },
          { id: 'third-parties', label: 'Third Parties', visible: canThirdParties },
        ] as const
      ).filter((t) => t.visible),
    [canContracts, canThirdParties],
  );

  const activeTab: TabKey | null = useMemo(() => {
    if (tabs.length === 0) return null;
    const explicit = searchParams.get('tab');
    if (explicit === 'data' && canContracts) return 'data';
    if (explicit === 'dashboard' && canContracts) return 'dashboard';
    if (explicit === 'third-parties' && canThirdParties) return 'third-parties';
    if (canContracts && DATA_ONLY_PARAMS.some((p) => searchParams.get(p))) return 'data';
    return tabs[0].id;
  }, [tabs, searchParams, canContracts, canThirdParties]);

  // If we landed here via a drill-down link (DATA_ONLY_PARAMS set but no
  // explicit ?tab=), stamp tab=data into the URL so the activeTab stays
  // on Data even after the user clears every filter — otherwise clearing
  // the last filter would surprise them by flipping back to the Dashboard.
  useEffect(() => {
    if (
      canContracts &&
      !searchParams.get('tab') &&
      DATA_ONLY_PARAMS.some((p) => searchParams.get(p))
    ) {
      const next = new URLSearchParams(searchParams);
      next.set('tab', 'data');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, canContracts]);

  const setTab = useCallback(
    (tab: TabKey) => {
      const next = new URLSearchParams(searchParams);
      next.set('tab', tab);
      // When leaving Data, clear its params so other tab views don't show
      // lingering filter state in the URL bar.
      if (tab !== 'data') {
        DATA_ONLY_PARAMS.forEach((p) => next.delete(p));
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  if (loading || !permissions) return null;

  if (!activeTab) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-semibold text-heading">Access Denied</h3>
          <p className="mt-1 text-sm text-muted">
            You don't have permission to access this page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full">
      {/* Tabs header */}
      <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700 px-6 pt-6">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === t.id
                ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'dashboard' && <ContractDashboard embedded />}
      {activeTab === 'data' && <ContractsList embedded />}
      {activeTab === 'third-parties' && <ThirdPartyList embedded />}
    </div>
  );
}
